import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = "/images/logo.png";

// Scroll-Funktion für Anchor-Navigation
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Events', section: 'events' },
    { label: 'Preise', section: 'pricing' },
    { label: 'FAQ', section: 'faq' },
    { label: 'Kontakt', section: 'contact' },
  ];

  const handleNavClick = (e, section) => {
    e.preventDefault();
    scrollToSection(section);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-gray-100' 
          : 'bg-white/70 backdrop-blur-md'
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#" 
            onClick={handleLogoClick}
            className="flex items-center" 
            data-testid="nav-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Geile Kiste Logo" 
              className="h-10 md:h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={`#${item.section}`}
                onClick={(e) => handleNavClick(e, item.section)}
                className="text-gray-600 hover:text-[#3ECDC3] transition-colors font-medium tracking-wide text-sm cursor-pointer"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <a
              href="tel:+4915223958867"
              className="flex items-center gap-2 bg-[#3ECDC3] text-white px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#2BB8AF] transition-all shadow-md hover:shadow-lg"
              data-testid="nav-phone"
            >
              <Phone size={16} />
              Anrufen
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700 p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-6 flex flex-col gap-4">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={`#${item.section}`}
                    onClick={(e) => handleNavClick(e, item.section)}
                    className="text-gray-600 hover:text-[#3ECDC3] transition-colors font-medium text-sm py-2 cursor-pointer"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href="tel:+4915223958867"
                  className="flex items-center justify-center gap-2 bg-[#3ECDC3] text-white px-4 py-3 rounded-full font-bold text-sm mt-4"
                >
                  <Phone size={16} />
                  +49 (0) 152 239 588 67
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};
